import { useEffect, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

const calendarClassNames = {
  root: 'w-full text-[var(--theme-text)]',
  months: 'grid gap-4',
  month: 'grid gap-3',
  month_caption: 'flex min-h-[38px] items-center justify-center px-12',
  caption_label: 'text-[0.96rem] font-semibold text-[var(--theme-text-strong)]',
  nav: 'absolute left-3 right-3 top-3 flex items-center justify-between',
  button_previous:
    'grid h-9 w-9 place-items-center rounded-xl border border-[var(--theme-border)] bg-white text-[var(--theme-accent)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-35',
  button_next:
    'grid h-9 w-9 place-items-center rounded-xl border border-[var(--theme-border)] bg-white text-[var(--theme-accent)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-35',
  chevron: 'h-4 w-4 fill-current',
  month_grid: 'w-full border-separate border-spacing-y-1',
  weekdays: 'grid grid-cols-7',
  weekday:
    'grid h-8 place-items-center text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]',
  week: 'grid grid-cols-7',
  day: 'grid h-9 place-items-center rounded-xl text-center text-[0.88rem] text-[var(--theme-text-strong)] transition [&>button]:h-9 [&>button]:w-9 [&>button]:rounded-xl [&>button]:transition hover:[&>button]:bg-[var(--theme-accent-soft)]',
  day_button: 'grid place-items-center outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)]',
  selected:
    'font-semibold text-white [&>button]:bg-[var(--theme-accent)] [&>button]:text-white hover:[&>button]:bg-[var(--theme-accent-strong)]',
  today:
    'font-semibold text-[var(--theme-accent)] [&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-[var(--theme-accent)]',
  outside: 'text-[#c3b7ad] opacity-60',
  disabled: 'cursor-not-allowed opacity-35 hover:[&>button]:bg-transparent',
};

function CustomDatePicker({
  label,
  name,
  value,
  error,
  onChange,
  placeholder = 'Select date',
  disabled = false,
  preferredPlacement = 'auto',
  focusMonth = '',
  highlightedDates = {},
  leadingIcon: LeadingIcon,
  leadingIconClassName,
  triggerShapeClassName = 'rounded-full',
  wrapperClassName = 'relative grid w-full min-w-0 self-start gap-1.5',
  labelClassName = 'text-[0.74rem] font-medium text-[var(--theme-text-subtle)]',
  errorClassName = 'text-[0.74rem] text-[#bb5745]',
  triggerClassName = '',
  popupAlign = 'left',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState('bottom');
  const [typedValue, setTypedValue] = useState('');
  const pickerRef = useRef(null);
  const triggerRef = useRef(null);
  const selectedDate = parseIsoDate(value);
  const focusedMonth = parseIsoMonth(focusMonth);
  const defaultCalendarMonth = selectedDate || focusedMonth || new Date();

  useEffect(() => {
    const nextSelectedDate = parseIsoDate(value);
    setTypedValue(nextSelectedDate ? formatDisplayDate(nextSelectedDate) : '');
  }, [value]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!pickerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function updatePlacement() {
      const triggerRect = triggerRef.current?.getBoundingClientRect();

      if (!triggerRect) {
        return;
      }

      const calendarHeight = 390;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      setPlacement(
        preferredPlacement === 'top' || preferredPlacement === 'bottom'
          ? preferredPlacement
          : spaceBelow < calendarHeight && spaceAbove > spaceBelow
            ? 'top'
            : 'bottom'
      );
    }

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);

    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [isOpen, preferredPlacement]);

  function handleSelect(day) {
    if (!day) {
      return;
    }

    setTypedValue(formatDisplayDate(day));
    onChange({
      target: {
        name,
        value: formatIsoDate(day),
      },
    });
    setIsOpen(false);
  }

  function handleTypedChange(event) {
    const nextValue = formatTypedDateInput(event.target.value);
    setTypedValue(nextValue);

    const parsedDate = parseTypedDate(nextValue, { strict: true });

    if (!parsedDate) {
      return;
    }

    onChange({
      target: {
        name,
        value: formatIsoDate(parsedDate),
      },
    });
  }

  function handleTypedBlur() {
    const parsedDate = parseTypedDate(typedValue);

    if (parsedDate) {
      setTypedValue(formatDisplayDate(parsedDate));
      return;
    }

    setTypedValue(selectedDate ? formatDisplayDate(selectedDate) : '');
  }

  function handleTypedKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleTypedBlur();
      setIsOpen(false);
    }
  }

  function handleTodaySelect() {
    handleSelect(new Date());
  }

  function handleClearSelect() {
    setTypedValue('');
    onChange({
      target: {
        name,
        value: '',
      },
    });
    setIsOpen(false);
  }

  return (
    <label className={wrapperClassName} ref={pickerRef}>
      <span className={labelClassName}>{label}</span>
      <div
        ref={triggerRef}
        className={`flex min-h-[40px] w-full min-w-0 items-center justify-between gap-2 ${triggerShapeClassName} border bg-white px-3 py-2 text-left text-[0.84rem] outline-none transition focus:ring-2 focus:ring-[var(--theme-accent-soft)] ${triggerClassName} ${
          error
            ? 'border-[#d78b7d] focus:border-[#c76552]'
            : 'border-[var(--theme-border)] focus:border-[var(--theme-accent)]'
        } ${selectedDate ? 'text-[var(--theme-text)]' : 'text-[var(--theme-text-muted)]'}`}
        role="group"
      >
        {LeadingIcon ? (
          <LeadingIcon
            className={leadingIconClassName || `h-4 w-4 shrink-0 ${error ? 'text-[#bb5745]' : 'text-[var(--theme-text-muted)]'}`}
            aria-hidden="true"
          />
        ) : null}
        <input
          className="min-w-0 flex-1 bg-transparent text-inherit outline-none placeholder:text-[var(--theme-text-muted)] disabled:cursor-not-allowed disabled:opacity-65"
          disabled={disabled}
          inputMode="numeric"
          maxLength={10}
          placeholder={placeholder}
          value={typedValue}
          onBlur={handleTypedBlur}
          onChange={handleTypedChange}
          onKeyDown={handleTypedKeyDown}
        />
        <button
          className="grid h-5 w-5 shrink-0 place-items-center rounded text-[var(--theme-accent)] outline-none transition hover:bg-[var(--theme-accent-soft)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          onClick={() => setIsOpen((currentValue) => !currentValue)}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-label={`Open ${label} calendar`}
        >
          <CalendarDays className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {error ? <span className={errorClassName}>{error}</span> : null}

      {isOpen ? (
        <div
          className={`absolute z-30 w-[min(328px,calc(100vw-2rem))] rounded-[22px] border border-[var(--theme-border)] bg-white p-3 shadow-[0_18px_45px_rgba(var(--theme-shadow-rgb),0.16)] ${
            popupAlign === 'right' ? 'right-0' : 'left-0'
          } ${
            placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          <DayPicker
            key={formatCalendarMonthKey(defaultCalendarMonth)}
            mode="single"
            selected={selectedDate}
            defaultMonth={defaultCalendarMonth}
            onSelect={handleSelect}
            showOutsideDays
            classNames={calendarClassNames}
            modifiers={{
              submitted: highlightedDates.submitted || [],
              draft: highlightedDates.draft || [],
            }}
            modifiersClassNames={{
              submitted:
                'font-semibold text-[#2f7a43] [&>button]:bg-[#eef9f0] [&>button]:text-[#2f7a43] [&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-[#bfe3c8] hover:[&>button]:bg-[#e2f4e6]',
              draft:
                'font-semibold text-[#9a6a12] [&>button]:bg-[#fff8e6] [&>button]:text-[#9a6a12] [&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-[#f0d49a] hover:[&>button]:bg-[#fff1c7]',
            }}
          />
          <div className="mt-2 flex items-center justify-between border-t border-[var(--theme-border)] pt-2">
            <button
              className="rounded-full border border-[var(--theme-border)] bg-white px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-text-strong)]"
              type="button"
              onClick={handleClearSelect}
            >
              Clear
            </button>
            <button
              className="rounded-full border border-[var(--theme-border)] bg-[var(--theme-surface-warm)] px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-accent-soft)] hover:text-[var(--theme-text-strong)]"
              type="button"
              onClick={handleTodaySelect}
            >
              Today
            </button>
          </div>
        </div>
      ) : null}
    </label>
  );
}

function parseIsoDate(value) {
  if (!value) {
    return undefined;
  }

  const [year, month, day] = String(value).split('-').map(Number);

  if (!year || !month || !day) {
    return undefined;
  }

  return new Date(year, month - 1, day);
}

function parseIsoMonth(value) {
  if (!value) {
    return undefined;
  }

  const [year, month] = String(value).split('-').map(Number);

  if (!year || !month) {
    return undefined;
  }

  return new Date(year, month - 1, 1);
}

function formatCalendarMonthKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}`;
}

function parseTypedDate(value, options = {}) {
  const normalizedValue = String(value || '').trim();
  const isStrict = Boolean(options.strict);
  const displayMatch = normalizedValue.match(
    isStrict ? /^(\d{2})-(\d{2})-(\d{4})$/ : /^(\d{1,2})-(\d{1,2})-(\d{4})$/
  );

  if (displayMatch) {
    return createValidDate(
      Number(displayMatch[3]),
      Number(displayMatch[1]),
      Number(displayMatch[2])
    );
  }

  return undefined;
}

function formatTypedDateInput(value) {
  const rawValue = String(value || '').replace(/[^\d-]/g, '');

  if (rawValue.includes('-')) {
    const dateParts = rawValue.split('-', 3);
    const [rawMonth = '', rawDay = '', rawYear = ''] = dateParts;
    const month = rawMonth.replace(/\D/g, '').slice(0, 2);
    const dayDigits = rawDay.replace(/\D/g, '');
    const day = dayDigits.slice(0, 2);
    const year = `${dayDigits.slice(2)}${rawYear.replace(/\D/g, '')}`.slice(0, 4);

    if (dateParts.length >= 3 || year) {
      return `${month}-${day}-${year}`.slice(0, 10);
    }

    return `${month}-${day}`.slice(0, 5);
  }

  const digits = rawValue.replace(/\D/g, '').slice(0, 8);
  const month = digits.slice(0, 2);
  const day = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  if (digits.length <= 2) {
    return month;
  }

  if (digits.length <= 4) {
    return `${month}-${day}`;
  }

  return `${month}-${day}-${year}`;
}

function createValidDate(year, month, day) {
  if (!year || !month || !day) {
    return undefined;
  }

  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }

  return date;
}

function formatIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function formatDisplayDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}-${day}-${year}`;
}

export default CustomDatePicker;
